import {
  normalizeFieldKey,
  parseKeyFromInput,
} from '../../create-template/extensions/fieldTokenEditorUtils';
import { formatDateBr, formatPersonalizedFormDataDates } from '../../../utils/dateBr';

export const CONTRACT_METHOD_STANDARD = 'standard';
export const CONTRACT_METHOD_PERSONALIZED = 'personalized';

const EMPTY_DOC = { type: 'doc', content: [] };

export function normalizeTemplateDocumentContent(raw) {
  if (!raw) return EMPTY_DOC;
  if (typeof raw === 'object' && raw.type === 'doc' && Array.isArray(raw.content)) {
    return raw;
  }
  if (typeof raw === 'object' && raw.content?.type === 'doc') {
    return raw.content;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return normalizeTemplateDocumentContent(parsed);
    } catch {
      return EMPTY_DOC;
    }
  }
  return EMPTY_DOC;
}

export function mapApiFieldsToFormFields(apiFields) {
  const seen = new Set();

  return (Array.isArray(apiFields) ? apiFields : [])
    .map((field) => {
      const id = parseKeyFromInput(field?.key ?? '');
      if (!id || seen.has(id)) return null;
      seen.add(id);

      return {
        id,
        key: field?.key ?? `{${id}}`,
        label: String(field?.name ?? id).trim() || id,
        required: Boolean(field?.required),
        type: String(field?.type ?? 'text').toLowerCase(),
      };
    })
    .filter(Boolean);
}

export function initializePersonalizedFormData(formFields) {
  return formFields.reduce((acc, field) => {
    acc[field.id] = '';
    return acc;
  }, {});
}

export function mergeSavedDataIntoPersonalizedForm(formFields, savedData) {
  const initialized = initializePersonalizedFormData(formFields);
  if (!savedData || typeof savedData !== 'object') return initialized;

  const merged = { ...initialized, ...savedData };

  (formFields ?? []).forEach((field) => {
    if (field.type !== 'date') return;
    const value = merged[field.id];
    if (value == null || value === '') return;
    merged[field.id] = formatDateBr(value);
  });

  return merged;
}

export function isCustomTemplateFillingType(doc) {
  const v = doc?.fillingType ?? doc?.filling_type;
  return String(v ?? '').trim().toLowerCase() === 'custom_template';
}

export function collectFieldKeysFromDocument(doc) {
  const keys = new Set();

  const walk = (nodes) => {
    (nodes ?? []).forEach((node) => {
      if (node?.type === 'fieldToken' && node.attrs?.key) {
        keys.add(normalizeFieldKey(node.attrs.key));
      }
      if (Array.isArray(node?.content)) {
        walk(node.content);
      }
    });
  };

  walk(doc?.content);
  return [...keys];
}

export function validatePersonalizedForm({
  documentTitle,
  selectedUserTemplateId,
  templateKey,
  formFields,
  formData,
}) {
  let requiredCount = 1;
  let filledCount = 0;

  if (documentTitle?.trim()) {
    filledCount += 1;
  }

  requiredCount += 1;
  if (selectedUserTemplateId && templateKey) {
    filledCount += 1;
  }

  formFields.forEach((field) => {
    if (!field.required) return;
    requiredCount += 1;
    const value = formData[field.id];
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      filledCount += 1;
    }
  });

  const isFormValid = requiredCount > 0 && filledCount === requiredCount;

  return {
    requiredFieldsCount: requiredCount,
    filledRequiredFields: filledCount,
    isFormValid,
  };
}

export function normalizeLoadedPersonalTemplate(template) {
  if (!template) return null;

  const content = normalizeTemplateDocumentContent(template.content);
  const formFields = mapApiFieldsToFormFields(template.fields);

  return {
    id: String(template.id ?? template.template_id ?? ''),
    name: String(template.name ?? '').trim(),
    templateKey: template.templateKey ?? template.template_key ?? null,
    content,
    formFields,
    fieldKeys: collectFieldKeysFromDocument(content),
  };
}

export function buildCustomTemplateContractPayload({
  templateKey,
  templateId,
  title,
  formData,
  formFields = [],
}) {
  const key = String(templateKey ?? '').trim();
  if (!key) {
    throw new Error('Template key inválido.');
  }

  const payload = {
    templateKey: key,
    title,
    fillingType: 'custom_template',
    dynamic_fields: [],
    data: formatPersonalizedFormDataDates(formData, formFields),
  };

  const id = String(templateId ?? '').trim();
  if (id) {
    payload.template_id = id;
  }

  return payload;
}
